import { useState, useEffect, useCallback } from 'react';
import { callApi } from '../../api/call.api';
import { companyApi } from '../../api/company.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner, Input, Select } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

export default function VoipTrunksPage() {
  const { addToast } = useUI();
  const [loading, setLoading] = useState(true);
  const [trunks, setTrunks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ trunk_id: null, trunk_name: '', label: '', company_id: '' });
  const [saving, setSaving] = useState(false);
  const [transferForm, setTransferForm] = useState({
    ami_host: '', ami_port: 5038, ami_username: '',
    transfer_context: 'from-internal', external_dial_prefix: '',
  });
  const [savingTransfer, setSavingTransfer] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [trunksRes, companiesRes, amiRes] = await Promise.all([
        callApi.getTrunkMappings(),
        companyApi.getCompanyList(),
        callApi.getVoipAmiConfig(),
      ]);
      setTrunks(trunksRes.data?.trunks || []);
      setCompanies(companiesRes.data || []);
      setTransferForm({
        ami_host: amiRes.data?.ami_host || '',
        ami_port: amiRes.data?.ami_port || 5038,
        ami_username: amiRes.data?.ami_username || '',
        transfer_context: amiRes.data?.transfer_context || 'from-internal',
        external_dial_prefix: amiRes.data?.external_dial_prefix || '',
      });
    } catch {
      addToast('خطا در دریافت اطلاعات', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  // Only the trunk list is safe to silently refresh on an interval - it
  // doesn't touch transferForm/companies, so it can't clobber the dispatcher
  // mid-edit on the transfer-settings inputs above.
  useAutoRefresh(() => {
    callApi.getTrunkMappings().then((res) => setTrunks(res.data?.trunks || [])).catch(() => {});
  }, { paused: !!form.trunk_id || saving });

  const handleSaveTransferSettings = async () => {
    setSavingTransfer(true);
    try {
      await callApi.saveVoipAmiConfig(transferForm);
      addToast('تنظیمات انتقال تماس ذخیره شد', 'success');
    } catch (e) {
      addToast(e.response?.data?.error || 'خطا در ذخیره', 'error');
    } finally {
      setSavingTransfer(false);
    }
  };

  const resetForm = () => setForm({ trunk_id: null, trunk_name: '', label: '', company_id: '' });

  const handleEdit = (t) => {
    setForm({ trunk_id: t.trunk_id, trunk_name: t.trunk_name, label: t.label || '', company_id: t.company_id || '' });
  };

  const handleSave = async () => {
    if (!form.trunk_name.trim()) {
      addToast('نام ترانک الزامی است', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await callApi.saveTrunkMapping({
        trunk_id: form.trunk_id,
        trunk_name: form.trunk_name.trim(),
        label: form.label.trim(),
        company_id: form.company_id || null,
      });
      setTrunks(res.data?.trunks || []);
      addToast('ذخیره شد', 'success');
      resetForm();
    } catch (e) {
      addToast(e.response?.data?.error || 'خطا در ذخیره', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trunk_id) => {
    try {
      const res = await callApi.deleteTrunkMapping(trunk_id);
      setTrunks(res.data?.trunks || []);
      addToast('حذف شد', 'success');
    } catch {
      addToast('خطا در حذف', 'error');
    }
  };

  const companyOptions = [{ value: '', label: '— بدون شرکت —' }, ...companies.map((c) => ({
    value: c.id, label: c.display_name || c.name,
  }))];

  return (
    <div className="fade-in">
      <PageHeader
        title="ترانک‌های VoIP"
        subtitle="مشخص کنید هر ترانک آسیریسک متعلق به کدام شرکت است تا هنگام تماس ورودی از آن ترانک، نام شرکت نمایش داده شود"
      />

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, maxWidth: 640, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>تنظیمات انتقال تماس (Transfer)</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 14, lineHeight: 1.8 }}>
          وقتی کارمندی روی پاپ‌آپ تماس ورودی دکمه «انتقال تماس» را بزند، این تنظیمات مشخص می‌کنند تماس در چه context ای از دایال‌پلن آستریسک و با چه پیشوندی برای شماره موبایل، منتقل شود.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Input
            label="Context دایال‌پلن برای انتقال"
            value={transferForm.transfer_context}
            onChange={(e) => setTransferForm((f) => ({ ...f, transfer_context: e.target.value }))}
            placeholder="from-internal"
            wrapStyle={{ flex: '1 1 220px' }}
          />
          <Input
            label="پیشوند شماره موبایل بیرونی (اختیاری)"
            value={transferForm.external_dial_prefix}
            onChange={(e) => setTransferForm((f) => ({ ...f, external_dial_prefix: e.target.value }))}
            placeholder="مثلاً 9"
            wrapStyle={{ flex: '1 1 220px' }}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <Btn variant="primary" size="sm" onClick={handleSaveTransferSettings} loading={savingTransfer}>ذخیره تنظیمات انتقال</Btn>
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, maxWidth: 640, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Input
            label="نام ترانک (مطابق نام peer در آسیریسک)"
            value={form.trunk_name}
            onChange={(e) => setForm((f) => ({ ...f, trunk_name: e.target.value }))}
            placeholder="مثلاً newrock-prnj-office"
            wrapStyle={{ flex: '1 1 220px' }}
          />
          <Input
            label="برچسب (اختیاری)"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="توضیح داخلی"
            wrapStyle={{ flex: '1 1 160px' }}
          />
          <Select
            label="شرکت"
            value={form.company_id}
            onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value }))}
            options={companyOptions}
            wrapStyle={{ flex: '1 1 200px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn variant="primary" onClick={handleSave} loading={saving}>
            {form.trunk_id ? 'به‌روزرسانی' : 'افزودن'}
          </Btn>
          {form.trunk_id && <Btn variant="ghost" onClick={resetForm}>انصراف</Btn>}
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', maxWidth: 640 }}>
          {trunks.length === 0 && (
            <div style={{ padding: 20, fontSize: 13, color: '#94a3b8' }}>هنوز ترانکی تعریف نشده است.</div>
          )}
          {trunks.map((t) => (
            <div key={t.trunk_id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
              borderBottom: '1px solid #f1f5f9',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{t.trunk_name}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {t.company_display || t.company_name || 'بدون شرکت'}{t.label ? ` · ${t.label}` : ''}
                </div>
              </div>
              <Btn variant="ghost" size="sm" onClick={() => handleEdit(t)}>ویرایش</Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDelete(t.trunk_id)}>حذف</Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
